import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { fetchFarmInformation, supabase } from '../supabase-client'
import './CoverSheetModal.css'

export default function CoverSheetModal({ isOpen, onClose, todaysOrders }) {
  const [currentPage, setCurrentPage] = useState(0)
  const [farmInfo, setFarmInfo] = useState(null)
  const [coverSheets, setCoverSheets] = useState([])

  const loadFarmInformation = async () => {
    try {
      const info = await fetchFarmInformation()
      setFarmInfo(info)
    } catch (error) {
      console.error('Error loading farm information:', error)
    }
  }

  const generateCoverSheets = useCallback(() => {
    // Group orders by customer and consignment
    const sheets = []
    
    todaysOrders.forEach(sale => {
      // Each sale becomes one cover sheet
      const sheet = {
        consignmentNo: sale.consignment_no,
        customerName: sale.customer_name,
        branchName: sale.branch_name,
        items: sale.items.map(item => ({
          itemNumber: item.item_number ? String(item.item_number) : 'N/A',
          strain: item.strain,
          quantity: `${item.quantity}kg`
        }))
      }
      sheets.push(sheet)
    })

    setCoverSheets(sheets)
    setCurrentPage(0)
  }, [todaysOrders])

  const goToPrevious = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1)
    }
  }, [currentPage])

  const goToNext = useCallback(() => {
    if (currentPage < coverSheets.length - 1) {
      setCurrentPage(currentPage + 1)
    }
  }, [currentPage, coverSheets.length])

  // Fetch farm information when modal opens
  useEffect(() => {
    if (isOpen) {
      loadFarmInformation()
      generateCoverSheets()
    }
  }, [isOpen, todaysOrders, generateCoverSheets])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!isOpen) return
      
      if (event.key === 'ArrowLeft') {
        goToPrevious()
      } else if (event.key === 'ArrowRight') {
        goToNext()
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [isOpen, currentPage, coverSheets.length, goToPrevious, goToNext])

  if (!isOpen || coverSheets.length === 0) return null

  const currentSheet = coverSheets[currentPage]

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="cover-sheet-modal !max-w-2xl" 
        style={{ zIndex: 60 }}
      >
        <DialogHeader>
          <DialogTitle>Cover Sheet Reference</DialogTitle>
        </DialogHeader>

        <div className="cover-sheet-content">
          {/* Cover Sheet Preview */}
          <div className="cover-sheet-preview">
            {/* Farm Logo */}
            <div className="logo-section">
              {farmInfo?.logo_file_path ? (() => {
                // Get the public URL for the logo from Supabase storage
                const { data } = supabase.storage
                  .from('farm-logos')
                  .getPublicUrl(farmInfo.logo_file_path)
                
                console.log('Logo public URL:', data.publicUrl)
                
                return (
                  <img 
                    src={data.publicUrl} 
                    alt={farmInfo.business_name || 'Farm Logo'}
                    className="farm-logo"
                    onError={(e) => {
                      console.log('Logo failed to load:', data.publicUrl)
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'block'
                    }}
                  />
                )
              })() : null}
              <div 
                className="farm-name-fallback" 
                style={{ display: farmInfo?.logo_file_path ? 'none' : 'block' }}
              >
                {farmInfo?.business_name || 'GMC'}
              </div>
            </div>

            {/* Content Section */}
            <div className="cover-sheet-body">
              {/* Left Side - Consignment Number */}
              <div className="consignment-section">
                <div className="consignment-number">
                  {currentSheet.consignmentNo}
                </div>
              </div>

              {/* Right Side - Items */}
              <div className="items-section">
                {currentSheet.items.map((item, index) => (
                  <div key={index} className="item-line">
                    <span className="item-number">{item.itemNumber}</span>
                    <span className="item-details">
                      {item.strain} - {item.quantity}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom - Customer Name */}
            <div className="customer-section">
              <div className="customer-name">
                {currentSheet.customerName}
                {currentSheet.branchName && (
                  <div className="branch-name">
                    {currentSheet.branchName}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="navigation-section">
            {/* Pagination Dots */}
            <div className="pagination-dots">
              {coverSheets.map((_, index) => (
                <button
                  key={index}
                  className={`pagination-dot ${index === currentPage ? 'active' : ''}`}
                  onClick={() => setCurrentPage(index)}
                />
              ))}
            </div>

            {/* Page Counter */}
            <div className="page-counter">
              Page {currentPage + 1} of {coverSheets.length}
            </div>
          </div>
        </div>

        <DialogFooter>
          <div className="navigation-buttons">
            <Button 
              variant="outline" 
              onClick={goToPrevious}
              disabled={currentPage === 0}
              className="nav-button"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              Previous
            </Button>
            
            <Button 
              variant="outline" 
              onClick={goToNext}
              disabled={currentPage === coverSheets.length - 1}
              className="nav-button"
            >
              Next
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
          </div>
          
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}